import { createMutation, createSuspenseQuery, makeQueryKey } from "@steto-care/steto-query";

import { PatientSyncRepository } from "@/io/repository/PatientSyncRepository.ts";
import { BulkCreateGroupedInclusionItem } from "@/models/GroupedInclusionModel.ts";
import { LibreViewClinicPatientsFilters, LibreViewIgnoreType } from "@/models/LibreViewModel.ts";
import { PractitionerQueries } from "@/queries/PractitionerQueries.ts";

export class PatientSyncQueries {
  constructor(
    private readonly practitioners: PractitionerQueries = new PractitionerQueries(),
    private readonly repository: PatientSyncRepository = new PatientSyncRepository(),
  ) {}

  /*******************************/
  /*********** QUERIES ***********/
  /*******************************/

  useLibreViewStatus = createSuspenseQuery(
    "patient-sync-libreview-status",
    () => makeQueryKey(),
    () => this.repository.getLibreViewStatus(),
  );

  useLibreViewClinicPatients = createSuspenseQuery(
    "patient-sync-libreview-clinic-patients",
    (filters: Partial<LibreViewClinicPatientsFilters>) =>
      makeQueryKey(filters.tirMax?.toString(), filters.tbrMin?.toString(), filters.lastDataMaxAgeDays?.toString()),
    (filters: LibreViewClinicPatientsFilters) => this.repository.getLibreViewClinicPatients(filters),
  );

  useGroupedInclusionDrafts = createSuspenseQuery(
    "patient-sync-grouped-inclusion-drafts",
    () => makeQueryKey(),
    () => this.repository.getGroupedInclusionDrafts(),
  );

  /*******************************/
  /********** MUTATIONS **********/
  /*******************************/

  useIgnoreLibreViewPatient = createMutation(
    "patient-sync-ignore-libreview-patient",
    (data: { email: string; ignoreType?: LibreViewIgnoreType }) =>
      this.repository.ignoreLibreViewPatient(data.email, data.ignoreType),
    {
      onSuccess: (_, __, { client }) => {
        this.useLibreViewClinicPatients.invalidate(client, {});
      },
    },
  );

  useBulkCreateGroupedInclusions = createMutation(
    "patient-sync-bulk-create-grouped-inclusions",
    (patients: BulkCreateGroupedInclusionItem[]) => this.repository.bulkCreateGroupedInclusions(patients),
    {
      onSuccess: (_, __, { client }) => {
        this.useLibreViewClinicPatients.invalidate(client, {});
        this.useGroupedInclusionDrafts.invalidate(client, {});
        this.practitioners.useGroupedInclusionPatients.invalidate(client, {});
      },
    },
  );

  useSetGroupedInclusionPhoneNumber = createMutation(
    "patient-sync-set-grouped-inclusion-phone-number",
    (data: { id: string; phoneNumber: string }) =>
      this.repository.setGroupedInclusionPhoneNumber(data.id, data.phoneNumber),
    {
      onSuccess: (_, __, { client }) => {
        this.useGroupedInclusionDrafts.invalidate(client, {});
      },
    },
  );

  useBulkSubmitGroupedInclusionDrafts = createMutation(
    "patient-sync-bulk-submit-grouped-inclusion-drafts",
    (patientIds: string[]) => this.repository.bulkSubmitGroupedInclusionDrafts(patientIds),
    {
      onSuccess: (_, __, { client }) => {
        this.useGroupedInclusionDrafts.invalidate(client, {});
        this.practitioners.useGroupedInclusionPatients.invalidate(client, {});
      },
    },
  );

  useDeleteGroupedInclusionDraft = createMutation(
    "patient-sync-delete-grouped-inclusion-draft",
    (id: string) => this.repository.deleteGroupedInclusionDraft(id),
    {
      onSuccess: (_, __, { client }) => {
        this.useGroupedInclusionDrafts.invalidate(client, {});
        this.practitioners.useGroupedInclusionPatients.invalidate(client, {});
      },
    },
  );

  useExportGroupedInclusionDraftsXls = createMutation("patient-sync-export-grouped-inclusion-drafts-xls", () =>
    this.repository.exportGroupedInclusionDraftsXls(),
  );
}
